import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import {
  Button,
  Form,
  Input,
  Select,
  Typography,
  Checkbox,
  InputNumber,
} from 'antd';
import { isValidPhoneNumber } from 'libphonenumber-js';
// API
import { GET_ALL_AREAS_OF_INFLUENCE } from 'api/areaOfInfluence/queries';
import { GET_ALL_PSYCHOSOCIAL_KEYWORDS } from 'api/psychosocialKeyword/queries';
import { GET_HASHTAGS } from 'api/hashtags/queries';
import { EDIT_STORE_ADMIN } from 'api/store/mutations';
// Constants
import { commonTimeFormat } from 'constants/global';
// Types
import {
  GetAllUserPsychosocialKeywords,
  GetAllUserPsychosocialKeywordsVariables,
} from 'api/psychosocialKeyword/types/GetAllUserPsychosocialKeywords';
import {
  GetAllAreasOfInfluence,
  GetAllAreasOfInfluenceVariables,
} from 'api/areaOfInfluence/types/GetAllAreasOfInfluence';
import {
  EditStoreAdmin,
  EditStoreAdminVariables,
} from 'api/store/types/EditStoreAdmin';
import {
  GetHashtags,
  GetHashtagsVariables,
} from 'api/hashtags/types/GetHashtags';
import { UploadFile } from 'antd/es/upload/interface';
import { GetManageBioStore_adminStores_entities } from 'api/store/types/GetManageBioStore';
import {
  StoreStatus,
  StoreEditAdminInput,
  OrganizationType,
} from 'api/graphql-global-types';
// Hooks
import { useGetAdmins, useGetSports } from 'hooks';
// Helpers
import {
  isEmptyString,
  isZero,
  ORGANIZATION_TYPE_OPTIONS,
} from 'helpers/editStore';
import { createLocation, handlePlaceSelect, joinLocation } from 'helpers/place';
// UI
import UploadImageFile from 'ui/UploadImageFile/UploadImageFile';
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import PhoneNumberInput from 'components/common/PhoneInput/PhoneNumberInput';
import RichText from 'components/common/RichText/RichText';
import CroppedPictureInput from 'components/common/CroppedPictureInput/CroppedPictureInput';
import PlaceInput from 'components/common/PlaceInput/PlaceInput';
import ProfileVisibilityInfo from 'components/common/ProfileVisibilityInfo';
// Styles
import './styles.scss';

const { Option } = Select;
const { Title } = Typography;

const buttonItemLayout = {
  wrapperCol: { span: 10, offset: 8 },
};

type EditOrganizationFormProps = {
  organization: GetManageBioStore_adminStores_entities | undefined;
};

type FormValues = {
  storeName?: string;
  sportType?: string[];
  country?: string;
  city?: string;
  uploadAvatarImage?: Array<UploadFile>;
  uploadCoverImage?: Array<UploadFile>;
  isOrphanPage: boolean;
  isForBrandDealsOnly: boolean;
  description?: string;
  hashtags?: number[];
  contactsFirstName?: string;
  contactsLastName?: string;
  contactsRole?: string;
  contactsEmail?: string;
  contactsPhoneNumber?: string;
  redirectSlugs?: string[];
  facebookLink: string;
  instagramLink: string;
  tiktokLink: string;
  website: string;
  twitterLink: string;
  socialMediaLink: string;
  youtubeLink: string;
  adConnected?: boolean;
  agencyName: string;
  agencyWebsite: string;
  agentEmail: string;
  agentName: string;
  facebookFollowing: number;
  inbound: string;
  initialAssessment: number;
  instagramFollowing: number;
  latestAssessment: number;
  managedById: string;
  socialConnected: boolean;
  tiktokFollowing: number;
  twitterFollowing: number;
  youtubeFollowing: number;
  areasOfInfluence?: string[];
  psychosocialKeywords?: string[];
  state: string;
  isBirthDayKnown: boolean;
  isBirthMonthKnown: boolean;
  birthDate: string;
  designRequestsPerMonthLimit: number;
  organizationType: OrganizationType | null;
  signing?: string | null;
  socialPost?: string | null;
  appearance?: string | null;
  pressConferenceOrAmplifiedInterview?: string | null;
  watchParty?: string | null;
  affinities?: string | null;
  currentSponsors?: string | null;
  conflictsOfInterestsOrNonInterests?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

const EditOrganizationForm = ({
  organization,
}: EditOrganizationFormProps): JSX.Element => {
  const [form] = Form.useForm();
  // eslint-disable-next-line
  const [formFields, setFormFields] = useState<FormValues | {}>({});
  const [pictureValidation, setPictureValidation] = useState<string>('');
  const uploadAvatarImage = form.getFieldValue('uploadAvatarImage');

  const { data: sportsData } = useGetSports();

  const { data: adminsData } = useGetAdmins({
    variables: {
      limit: 100,
      status: [StoreStatus.Active, StoreStatus.Pending, StoreStatus.Inactive],
    },
  });

  useEffect(() => {
    if (organization) {
      form.setFieldsValue({
        storeName: organization.storeDetails?.storeName,
        sportType: organization.sports.map((item) => item.id.toString()),
        country: organization.storeDetails?.country,
        state: organization.storeDetails?.state,
        city: organization.storeDetails?.city,
        uploadAvatarImage: organization.storeDetails?.avatarURL,
        uploadCoverImage: organization.storeDetails?.coverURL,
        isOrphanPage: organization.isOrphanPage,
        isForBrandDealsOnly: organization.isForBrandDealsOnly,
        description: organization.storeDetails?.description,
        hashtags: organization.hashtags.map((item) => item.id),
        contactsFirstName: organization.firstName,
        contactsLastName: organization.lastName,
        contactsRole: organization?.storeDetails?.gymRole,
        organizationType: organization?.storeDetails?.organizationType || null,
        contactsEmail: organization.email,
        contactsPhoneNumber: organization.phoneNumber,
        redirectSlugs: organization.redirectSlugs || [],
        facebookLink: organization?.storeDetails?.facebookLink,
        instagramLink: organization?.storeDetails?.instagramLink,
        tiktokLink: organization?.storeDetails?.tiktokLink,
        youtubeLink: organization?.storeDetails?.youtubeLink,
        twitterLink: organization?.storeDetails?.twitterLink,
        website: organization?.storeDetails?.website,
        adConnected: organization?.storeManagementDetails?.adConnected,
        agencyName: organization.storeManagementDetails?.agencyName,
        agencyWebsite: organization.storeManagementDetails?.agencyWebsite,
        agentEmail: organization.storeManagementDetails?.agentEmail,
        agentName: organization.storeManagementDetails?.agentName,
        inbound: organization?.storeManagementDetails?.inbound,
        initialAssessment:
          organization?.storeManagementDetails?.initialAssessment,
        latestAssessment:
          organization?.storeManagementDetails?.latestAssessment,
        managedById: organization?.storeManagementDetails?.managedById,
        socialConnected: organization?.storeManagementDetails?.socialConnected,
        facebookFollowing: organization?.storeDetails?.facebookFollowing,
        instagramFollowing: organization?.storeDetails?.instagramFollowing,
        tiktokFollowing: organization?.storeDetails?.tiktokFollowing,
        twitterFollowing: organization?.storeDetails?.twitterFollowing,
        youtubeFollowing: organization?.storeDetails?.youtubeFollowing,
        areasOfInfluence: organization.areasOfInfluence?.map((item) => item.id),
        psychosocialKeywords: organization.psychosocialKeywords?.map(
          (item) => item.id
        ),
        designRequestsPerMonthLimit:
          organization?.storeManagementDetails?.designRequestsPerMonthLimit,
        signing: organization.storeManagementDetails?.signing || null,
        socialPost: organization.storeManagementDetails?.socialPost || null,
        appearance: organization.storeManagementDetails?.appearance || null,
        pressConferenceOrAmplifiedInterview:
          organization.storeManagementDetails
            ?.pressConferenceOrAmplifiedInterview || null,
        watchParty: organization.storeManagementDetails?.watchParty || null,
        affinities: organization.storeManagementDetails?.affinities || null,
        currentSponsors:
          organization.storeManagementDetails?.currentSponsors || null,
        conflictsOfInterestsOrNonInterests:
          organization.storeManagementDetails
            ?.conflictsOfInterestsOrNonInterests || null,
        citySearch: joinLocation([
          organization?.storeDetails?.city,
          organization?.storeDetails?.state,
          organization?.storeDetails?.country,
        ]),
        latitude: organization?.storeDetails?.latitude || null,
        longitude: organization?.storeDetails?.longitude || null,
      });
    }
  }, [organization, form]);

  const { data } = useQuery<GetHashtags, GetHashtagsVariables>(GET_HASHTAGS, {
    variables: {
      keyword: '',
    },
    fetchPolicy: 'cache-and-network',
  });

  const [editStoreAdmin, { loading: editLoading }] = useMutation<
    EditStoreAdmin,
    EditStoreAdminVariables
  >(EDIT_STORE_ADMIN);

  const { data: areaOfInfluenceData } = useQuery<
    GetAllAreasOfInfluence,
    GetAllAreasOfInfluenceVariables
  >(GET_ALL_AREAS_OF_INFLUENCE, {
    variables: {
      input: {
        keyword: '',
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: psychosocialKeywordData } = useQuery<
    GetAllUserPsychosocialKeywords,
    GetAllUserPsychosocialKeywordsVariables
  >(GET_ALL_PSYCHOSOCIAL_KEYWORDS, {
    variables: {
      input: {
        keyword: '',
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const handleFormSubmit = async (values: FormValues): Promise<void> => {
    const avatar =
      values.uploadAvatarImage?.length === 0
        ? null
        : values?.uploadAvatarImage?.[0]?.originFileObj;
    const cover =
      values.uploadCoverImage?.length === 0
        ? null
        : values?.uploadCoverImage?.[0]?.originFileObj;

    const storeEditInput: StoreEditAdminInput = {
      storeId: organization?.id || '',
      userDetails: {
        firstName: values.contactsFirstName,
        lastName: values.contactsLastName,
        email: values.contactsEmail || '',
        phoneNumberE164: values.contactsPhoneNumber || '',
        sportIds: values?.sportType?.map(Number),
        hashtagIds: values.hashtags,
        isOrphanPage: values.isOrphanPage,
        isForBrandDealsOnly: values.isForBrandDealsOnly,
        redirects: values.redirectSlugs || [],
        areaOfInfluenceIds: values.areasOfInfluence || [],
        psychosocialKeywordsIds: values.psychosocialKeywords || [],
      },
      storeDetails: {
        socialMediaLink: values.socialMediaLink,
        storeName: values.storeName,
        facebookLink: isEmptyString(values.facebookLink),
        instagramLink: isEmptyString(values.instagramLink),
        tiktokLink: isEmptyString(values.tiktokLink),
        youtubeLink: isEmptyString(values.youtubeLink),
        twitterLink: isEmptyString(values.twitterLink),
        website: isEmptyString(values.website),
        ...(values.uploadAvatarImage === organization?.storeDetails?.avatarURL
          ? {}
          : { avatar: avatar }),
        ...(values.uploadCoverImage === organization?.storeDetails?.coverURL
          ? {}
          : { cover: cover }),
        location: createLocation(values.latitude, values.longitude),
        description: values.description || '',
        country: values.country || null,
        state: values.state || null,
        city: values.city || null,
        gymRole: values.contactsRole || '',
        organizationType: values.organizationType || null,
      },
      storeManagementDetails: {
        adConnected: values.adConnected || false,
        agencyName: isEmptyString(values.agencyName),
        agencyWebsite: isEmptyString(values.agencyWebsite),
        agentEmail: isEmptyString(values.agentEmail),
        agentName: isEmptyString(values.agentName),
        inbound: isEmptyString(values.inbound),
        managedById: values.managedById || null,
        socialConnected: values.socialConnected,
        initialAssessment: isZero(
          Number(values.initialAssessment) <= 5 &&
            Number(values.initialAssessment) >= 0
            ? Number(values.initialAssessment)
            : 0
        ),
        latestAssessment: isZero(
          Number(values.latestAssessment) <= 5 &&
            Number(values.latestAssessment) >= 0
            ? Number(values.latestAssessment)
            : 0
        ),
        instagramFollowing: isZero(Number(values.instagramFollowing)),
        facebookFollowing: isZero(Number(values.facebookFollowing)),
        tiktokFollowing: isZero(Number(values.tiktokFollowing)),
        twitterFollowing: isZero(Number(values.twitterFollowing)),
        youtubeFollowing: isZero(Number(values.youtubeFollowing)),
        designRequestsPerMonthLimit:
          Number(values.designRequestsPerMonthLimit) || 1,
        signing: values.signing || null,
        socialPost: values.socialPost || null,
        appearance: values.appearance || null,
        pressConferenceOrAmplifiedInterview:
          values.pressConferenceOrAmplifiedInterview || null,
        watchParty: values.watchParty || null,
        affinities: values.affinities || null,
        currentSponsors: values.currentSponsors || null,
        conflictsOfInterestsOrNonInterests:
          values.conflictsOfInterestsOrNonInterests || null,
      },
    };

    try {
      const res = await editStoreAdmin({
        variables: {
          input: storeEditInput,
        },
      });

      const errors = res?.data?.editStoreAdmin?.errors;

      if (errors && errors.length > 0) {
        const errorMessages = errors.map((error) => error).join(', ');
        return errorNotification(`${errorMessages}`);
      }

      successNotification(`The description has been updated`);
    } catch (err) {
      if (
        (err as any).graphQLErrors[0]?.extensions.response?.message.some(
          (item: string | string[]) =>
            item.includes('must be a valid phone number')
        )
      ) {
        return errorNotification('Please enter a valid phone number');
      } else {
        errorNotification();
        console.log('update description error:', { ...(err as Error) });
      }
    }
  };

  const adminsOptions = adminsData?.admins?.entities.map(
    ({ id, firstName, lastName, status }) => {
      const isInactive = status === StoreStatus.Inactive;
      return (
        <Option key={id} value={id}>
          <div className={isInactive ? 'isDisabled' : ''}>
            {`${firstName} ${lastName} ${isInactive ? 'INACTIVE' : ''}`}
          </div>
        </Option>
      );
    }
  );

  const areasOfInfluenceOptions =
    areaOfInfluenceData?.getAllAreasOfInfluence?.entities.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));

  const psychosocialKeywordOptions =
    psychosocialKeywordData?.getAllUserPsychosocialKeywords?.entities.map(
      (item) => (
        <Option key={item.id} value={item.id}>
          {item.name}
        </Option>
      )
    );

  const hashtagsOptions = data?.getHashtags.map((item) => (
    <Option key={item.id} value={item.id}>
      {item.name}
    </Option>
  ));

  const redirectOptions = organization?.redirectSlugs?.map((item) => (
    <Option key={item} value={item}>
      {item}
    </Option>
  ));

  const isOrganizationInPendingStatus = !organization?.activatedAt;
  const sports = sportsData?.sports || [];

  const handleChangeDescription = (value: string): void => {
    form.setFieldsValue({ description: value });
  };

  const normFile = (e: any) => {
    if (e && Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const setFields = () => {
    const fields = form.getFieldsValue();
    setFormFields({
      ...fields,
    });
  };

  const onPhoneNumberChange = (value: string) =>
    form.setFieldsValue({ contactsPhoneNumber: `+${value}` });

  return (
    <div className="formWrapper">
      <Form
        onFinish={handleFormSubmit}
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 10 }}
        onValuesChange={setFields}
        scrollToFirstError
      >
        <Form.Item>
          <Title level={5}>General</Title>
        </Form.Item>
        <>
          <Form.Item name="storeName" label="Organization Name">
            <Input
              placeholder="Enter gym name"
              disabled={isOrganizationInPendingStatus}
            />
          </Form.Item>
          <Form.Item name="socialMediaLink" label="Social Media Link">
            <Input placeholder="Enter social media link" />
          </Form.Item>

          <Form.Item name="facebookLink" label="Facebook link">
            <Input placeholder="Enter facebook link" />
          </Form.Item>

          <Form.Item name="facebookFollowing" label="Facebook following">
            <Input type="number" placeholder="Facebook following" />
          </Form.Item>

          <Form.Item name="instagramLink" label="Instagram link">
            <Input placeholder="Enter instagram link" />
          </Form.Item>

          <Form.Item name="instagramFollowing" label="Instagram Following">
            <Input type="number" placeholder="Instagram Following" />
          </Form.Item>

          <Form.Item name="tiktokLink" label="Tiktok link">
            <Input placeholder="Enter tiktok link" />
          </Form.Item>

          <Form.Item name="tiktokFollowing" label="Tiktok Following">
            <Input type="number" placeholder="Tiktok Following" />
          </Form.Item>

          <Form.Item name="twitterLink" label="Twitter link">
            <Input placeholder="Enter Twitter link" />
          </Form.Item>

          <Form.Item name="twitterFollowing" label="Twitter Following">
            <Input type="number" placeholder="Twitter Following" />
          </Form.Item>

          <Form.Item name="youtubeLink" label="Youtube link">
            <Input placeholder="Enter Youtube link" />
          </Form.Item>

          <Form.Item name="youtubeFollowing" label="Youtube Following">
            <Input type="number" placeholder="Youtube Following" />
          </Form.Item>

          <Form.Item name="website" label="Website link">
            <Input placeholder="Enter website link" />
          </Form.Item>
          <Form.Item
            name="sportType"
            label="Type of sport"
            rules={[
              {
                required: true,
                message: 'Please, select sport',
              },
            ]}
          >
            <Select
              id="sportType"
              placeholder="Select sport type"
              mode="multiple"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {sports.map((item) => {
                return (
                  <Option value={`${item.id}`} key={item.id}>
                    {item.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="uploadAvatarImage"
            label="Profile Image"
            validateStatus={
              pictureValidation ||
              form.getFieldsError(['uploadAvatarImage'])[0].errors?.length ||
              !uploadAvatarImage
                ? 'error'
                : ''
            }
            help={pictureValidation || null}
            rules={[
              {
                required: true,
                message: 'Please upload the picture',
              },
            ]}
          >
            <CroppedPictureInput
              setPictureValidation={setPictureValidation}
              defaultImage={organization?.storeDetails?.avatarURL}
              requiredMessage="Please upload the picture"
              buttonTitle="Upload profile picture"
            />
          </Form.Item>

          <Form.Item
            name="uploadCoverImage"
            label="Background image"
            valuePropName="file"
            getValueFromEvent={normFile}
          >
            <UploadImageFile
              hideButton={
                form.getFieldsValue()?.uploadCoverImage?.length >= 1 ||
                form.getFieldInstance('uploadCoverImage')?.fileList?.length
              }
              buttonTitle="Upload image"
              imageAlt="Background image"
              defaultFileUrl={organization?.storeDetails?.coverURL}
            />
          </Form.Item>
          <Form.Item
            name="isOrphanPage"
            label="Hide my profile"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item
            name="isForBrandDealsOnly"
            label={
              <span>
                Is For Brand Deals Only
                <ProfileVisibilityInfo />
              </span>
            }
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <div className="richTextWrapper">
              <RichText
                onChange={handleChangeDescription}
                placeholder="Add organization description"
                initialValue={organization?.storeDetails?.description || ''}
              />
            </div>
          </Form.Item>
          <Form.Item
            name="designRequestsPerMonthLimit"
            label="Request Merch Design limit"
            tooltip="How many times per month organization can request merch design, default value is 1"
          >
            <Input
              type="number"
              placeholder="Enter Request Merch Design limit per month"
              min={1}
              max={100}
            />
          </Form.Item>

          <Title level={5}>Store management</Title>

          <div className="liveDateWrapper">
            <Title level={5}>
              Live date:
              {organization?.storeManagementDetails?.liveDate
                ? moment(organization?.storeManagementDetails?.liveDate).format(
                    commonTimeFormat
                  )
                : ' Date unavailable'}
            </Title>
          </div>

          <Form.Item name="managedById" label="Managed by:">
            <Select allowClear placeholder="Managed by">
              {adminsOptions}
            </Select>
          </Form.Item>

          <Form.Item
            name="adConnected"
            label="Ad connected"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>

          <Form.Item
            name="socialConnected"
            label="Social connected"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>

          <Form.Item name="agencyName" label="Agency Name">
            <Input placeholder="Enter agency name" />
          </Form.Item>

          <Form.Item name="agencyWebsite" label="Agency Website">
            <Input placeholder="Enter agency Website" />
          </Form.Item>

          <Form.Item name="agentEmail" label="Agent email">
            <Input placeholder="Enter agent email" />
          </Form.Item>

          <Form.Item name="agentName" label="Agent Name">
            <Input placeholder="Enter agent name" />
          </Form.Item>

          <Form.Item name="inbound" label="Inbound">
            <Input placeholder="Enter Inbound" />
          </Form.Item>

          <Form.Item name="initialAssessment" label="Initial Assessment">
            <InputNumber
              type="number"
              placeholder="Initial Assessment"
              min={0}
              max={5}
            />
          </Form.Item>

          <Form.Item name="latestAssessment" label="Latest Assessment">
            <InputNumber
              type="number"
              placeholder="Latest Assessment"
              min={0}
              max={5}
            />
          </Form.Item>

          <Form.Item name="hashtags" label="Hashtags">
            <Select
              placeholder="Select hashtags"
              mode="multiple"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {hashtagsOptions}
            </Select>
          </Form.Item>

          <Form.Item name="areasOfInfluence" label="Areas of interest">
            <Select
              placeholder="Select areas"
              mode="multiple"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {areasOfInfluenceOptions}
            </Select>
          </Form.Item>

          <Form.Item name="psychosocialKeywords" label="Internal tags">
            <Select
              placeholder="Select psychosocial keywords"
              mode="multiple"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {psychosocialKeywordOptions}
            </Select>
          </Form.Item>
        </>

        <Form.Item>
          <Title level={5} style={{ marginTop: '16px' }}>
            Contact person
          </Title>
        </Form.Item>
        <>
          <Form.Item
            name="contactsFirstName"
            label="Contact's first Name"
            rules={[
              {
                required: true,
                message: 'Please, input first name',
              },
            ]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>
          <Form.Item
            name="contactsLastName"
            label="Contact's last Name"
            rules={[
              {
                required: true,
                message: 'Please, input last name',
              },
            ]}
          >
            <Input placeholder="Enter last name" />
          </Form.Item>

          <Form.Item name="organizationType" label="Organization Type">
            <Select placeholder="Select organization type">
              {ORGANIZATION_TYPE_OPTIONS.map((item) => {
                return (
                  <Option value={`${item.value}`} key={item.value}>
                    {item.label}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item name="contactsRole" label="Contact's role">
            <Input placeholder="Enter role" />
          </Form.Item>
          <Form.Item
            name="contactsEmail"
            label="Contact's email"
            rules={[
              {
                required: true,
                message: 'Please, input email',
              },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            name="contactsPhoneNumber"
            label="Contact's phone number"
            rules={[
              {
                required: true,
                message: 'Please, input phone number',
              },
              {
                message: 'Phone must be a valid phone number',
                validator: async (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }

                  if (isValidPhoneNumber(value)) {
                    return Promise.resolve();
                  }

                  return Promise.reject();
                },
              },
            ]}
          >
            <PhoneNumberInput
              name="contactsPhoneNumber"
              value={organization?.phoneNumber || ' '}
              placeholder="Enter phone number"
              handlePhoneChange={onPhoneNumberChange}
            />
          </Form.Item>

          <Form.Item name="country" label="Country">
            <Input disabled />
          </Form.Item>

          {form.getFieldValue('state') && (
            <Form.Item name="state" label="State">
              <Input disabled />
            </Form.Item>
          )}

          <Form.Item name="city" label="City">
            <Input disabled />
          </Form.Item>

          <Form.Item name="latitude" noStyle />
          <Form.Item name="longitude" noStyle />

          <Form.Item name="citySearch" label="Enter location">
            <PlaceInput
              onPlaceSelect={(placeDetails) =>
                handlePlaceSelect(form.setFieldsValue, placeDetails, 'city')
              }
            />
          </Form.Item>

          <Form.Item name="redirectSlugs" label="List of redirect names">
            <Select mode="tags" placeholder="Enter redirect name">
              {redirectOptions}
            </Select>
          </Form.Item>

          <Title level={5}>Sponsor pricing</Title>

          <Form.Item
            name="signing"
            label="Signing (hourly rate or price per item and minimums)"
          >
            <Input placeholder="Signing (hourly rate or price per item and minimums)" />
          </Form.Item>

          <Form.Item name="appearance" label="Appearance (hourly rate)">
            <Input placeholder="Appearance (hourly rate)" />
          </Form.Item>

          <Form.Item name="socialPost" label="Social Post (price per post)">
            <Input placeholder="Social Post (price per post)" />
          </Form.Item>

          <Form.Item
            name="pressConferenceOrAmplifiedInterview"
            label="Press Conference / Amplified Interview"
          >
            <Input placeholder="Press Conference / Amplified Interview" />
          </Form.Item>

          <Form.Item name="watchParty" label="Watch Party">
            <Input placeholder="Watch Party" />
          </Form.Item>

          <Form.Item name="affinities" label="Affinities">
            <Input placeholder="Affinities" />
          </Form.Item>

          <Form.Item name="currentSponsors" label="Current Sponsors">
            <Input placeholder="Current Sponsors" />
          </Form.Item>

          <Form.Item
            name="conflictsOfInterestsOrNonInterests"
            label="Conflicts of interest / Non-interests"
          >
            <Input placeholder="Conflicts of interest / Non-interests" />
          </Form.Item>
        </>

        <Form.Item {...buttonItemLayout}>
          <Button type="primary" htmlType="submit" loading={editLoading}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EditOrganizationForm;
